import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store';
import { authAPI, teamAPI, workHoursAPI, timeOffAPI, timeTrackingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  Loader2, Lock, Eye, EyeOff, UserCircle, Mail, Shield, Phone, Building,
  Clock, Palmtree, Cake, Plus, Trash2, Pencil, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = { ceo: 'CEO', manager: 'Manager', member: 'Equipo', client: 'Cliente' };

export default function ProfilePage() {
  const { user } = useAuthStore();
  const isInternal = user?.role !== 'client';

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const [info, setInfo] = useState({ phone: '', department: '', position: '', birthday_day: '', birthday_month: '' });
  const [savingInfo, setSavingInfo] = useState(false);

  const [hours, setHours] = useState([]);
  const [hoursForm, setHoursForm] = useState({ date: new Date().toISOString().split('T')[0], hours: '', project: '', note: '' });

  const [timeOff, setTimeOff] = useState([]);
  const [toForm, setToForm] = useState({ start_date: '', end_date: '', type: 'vacation', reason: '' });

  const [myMonth, setMyMonth] = useState(new Date().toISOString().slice(0, 7));
  const [myMonthly, setMyMonthly] = useState(null);
  const [editingLog, setEditingLog] = useState(null);
  const [editMin, setEditMin] = useState('');

  const reloadMonthly = () => timeTrackingAPI.getMyMonthly(myMonth).then(setMyMonthly).catch(() => {});

  const saveLogEdit = async (logId) => {
    const mins = parseInt(editMin, 10);
    if (isNaN(mins) || mins < 0) { toast.error('Minutos no válidos'); return; }
    try {
      await timeTrackingAPI.updateLog(logId, mins);
      toast.success('Registro actualizado');
      setEditingLog(null);
      reloadMonthly();
    } catch (e) { toast.error(e.message || 'No se pudo actualizar'); }
  };

  const deleteLog = async (logId) => {
    try {
      await timeTrackingAPI.deleteLog(logId);
      toast.success('Registro eliminado');
      reloadMonthly();
    } catch (e) { toast.error(e.message || 'No se pudo eliminar'); }
  };

  useEffect(() => {
    if (!isInternal) return;
    timeTrackingAPI.getMyMonthly(myMonth).then(setMyMonthly).catch(() => setMyMonthly(null));
  }, [myMonth, isInternal]);

  const initials = (user?.name || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  useEffect(() => {
    if (!isInternal || !user?.id) return;
    (async () => {
      try {
        const me = await teamAPI.getMember(user.id);
        setInfo({
          phone: me.profile?.phone || '',
          department: me.profile?.department || '',
          position: me.profile?.position || '',
          birthday_day: me.profile?.birthday_day || '',
          birthday_month: me.profile?.birthday_month || '',
        });
      } catch (e) { /* ignore */ }
      const [h, t] = await Promise.all([
        workHoursAPI.getAll().catch(() => []),
        timeOffAPI.getAll().catch(() => []),
      ]);
      setHours(h);
      setTimeOff(t);
    })();
  }, [user?.id, isInternal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (next !== confirm) { toast.error('Las contraseñas no coinciden'); return; }
    if (next === current) { toast.error('La nueva contraseña debe ser diferente de la actual'); return; }
    setSaving(true);
    try {
      await authAPI.changePassword(current, next);
      toast.success('Contraseña actualizada correctamente');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.message || 'No se pudo cambiar la contraseña');
    } finally { setSaving(false); }
  };

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await teamAPI.updateDirectory(user.id, {
        phone: info.phone || undefined,
        department: info.department || undefined,
        position: info.position || undefined,
        birthday_day: info.birthday_day ? parseInt(info.birthday_day, 10) : undefined,
        birthday_month: info.birthday_month ? parseInt(info.birthday_month, 10) : undefined,
      });
      toast.success('Información actualizada');
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar');
    } finally { setSavingInfo(false); }
  };

  const addHours = async () => {
    if (!hoursForm.date || !hoursForm.hours) { toast.error('Indica fecha y horas'); return; }
    try {
      await workHoursAPI.create({ date: hoursForm.date, hours: parseFloat(hoursForm.hours), project: hoursForm.project || null, note: hoursForm.note || null });
      toast.success('Horas registradas');
      setHoursForm({ date: new Date().toISOString().split('T')[0], hours: '', project: '', note: '' });
      setHours(await workHoursAPI.getAll());
    } catch (e) { toast.error(e.message || 'No se pudo registrar'); }
  };

  const deleteHours = async (id) => {
    try { await workHoursAPI.delete(id); setHours(await workHoursAPI.getAll()); } catch (e) { toast.error(e.message); }
  };

  const requestTimeOff = async () => {
    if (!toForm.start_date || !toForm.end_date) { toast.error('Indica fecha de inicio y fin'); return; }
    try {
      await timeOffAPI.request(toForm);
      toast.success('Solicitud enviada');
      setToForm({ start_date: '', end_date: '', type: 'vacation', reason: '' });
      setTimeOff(await timeOffAPI.getAll());
    } catch (e) { toast.error(e.message || 'No se pudo enviar'); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto space-y-6 p-1" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold">Mi Perfil</h1>
        <p className="text-muted-foreground text-sm">Gestiona tu cuenta, tu información y tus horas.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><UserCircle className="w-5 h-5 text-primary" /> Datos de la cuenta</CardTitle></CardHeader>
        <CardContent className="flex items-center gap-4">
          <Avatar className="w-16 h-16"><AvatarImage src={user?.avatar} /><AvatarFallback className="text-lg bg-primary/10 text-primary">{initials}</AvatarFallback></Avatar>
          <div className="space-y-1">
            <p className="text-lg font-semibold" data-testid="profile-name">{user?.name}</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {user?.email}</p>
            <Badge variant="outline" className="mt-1"><Shield className="w-3 h-3 mr-1" /> {ROLE_LABELS[user?.role] || user?.role}</Badge>
          </div>
        </CardContent>
      </Card>

      {isInternal && (
        <>
          {/* Mi informacion */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Building className="w-5 h-5 text-primary" /> Mi información</CardTitle>
              <CardDescription>Estos datos aparecen en el Directorio y en Cumpleaños del equipo.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4" data-testid="my-info-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Teléfono</Label><Input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} placeholder="+34 600 000 000" data-testid="info-phone-input" /></div>
                <div className="space-y-2"><Label className="flex items-center gap-1.5"><Building className="w-3.5 h-3.5" /> Área</Label><Input value={info.department} onChange={(e) => setInfo({ ...info, department: e.target.value })} placeholder="Contenido, Ads..." data-testid="info-department-input" /></div>
              </div>
              <div className="space-y-2"><Label>Puesto</Label><Input value={info.position} onChange={(e) => setInfo({ ...info, position: e.target.value })} placeholder="Copywriter, Diseñador..." data-testid="info-position-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" /> Cumpleaños (día)</Label><Input type="number" min={1} max={31} value={info.birthday_day} onChange={(e) => setInfo({ ...info, birthday_day: e.target.value })} data-testid="info-bday-day-input" /></div>
                <div className="space-y-2"><Label>Cumpleaños (mes)</Label><Input type="number" min={1} max={12} value={info.birthday_month} onChange={(e) => setInfo({ ...info, birthday_month: e.target.value })} data-testid="info-bday-month-input" /></div>
              </div>
              <Button onClick={saveInfo} disabled={savingInfo} data-testid="save-info-btn">{savingInfo ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : 'Guardar información'}</Button>
            </CardContent>
          </Card>

          {/* Registro de tiempo → página dedicada (evita duplicar) */}
          <Card data-testid="profile-time-tracking-link">
            <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Clock className="w-5 h-5 text-primary" /></div>
                <div>
                  <p className="font-semibold">Registro de tiempo</p>
                  <p className="text-sm text-muted-foreground">Consulta tus horas, fichajes y desglose en tu página de Registro de Tiempo.</p>
                </div>
              </div>
              <a href="/time-tracking"><Button variant="outline" data-testid="go-time-tracking-btn">Abrir Registro de Tiempo</Button></a>
            </CardContent>
          </Card>

          {/* Ausencias → panel de equipo (marcar y ver ausencias) */}
          <Card data-testid="profile-absences-link">
            <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Palmtree className="w-5 h-5 text-primary" /></div>
                <div>
                  <p className="font-semibold">Ausencias</p>
                  <p className="text-sm text-muted-foreground">Avisa de los días que estarás fuera y consulta las ausencias del equipo.</p>
                </div>
              </div>
              <a href="/time-off"><Button variant="outline" data-testid="go-absences-btn">Abrir Ausencias</Button></a>
            </CardContent>
          </Card>
        </>
      )}

      {/* Cambiar contrasena */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-primary" /> Cambiar contraseña</CardTitle>
          <CardDescription>Introduce tu contraseña actual y la nueva (mínimo 8 caracteres).</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current">Contraseña actual</Label>
              <Input id="current" type={show ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="next">Nueva contraseña</Label>
              <div className="relative">
                <Input id="next" type={show ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)} placeholder="Mínimo 8 caracteres" required minLength={8} data-testid="new-password-input" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="toggle-password-visibility">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirma la nueva contraseña</Label>
              <Input id="confirm" type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={saving} data-testid="change-password-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : 'Actualizar contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
}
